import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { add, multiply, round } from 'mathjs';

import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useConfig from '../../context/ConfigContext';
import {
  FormHeaderUI,
  FormButtonPanelUI,
  DeleteDialogUI,
  ButtonUI,
  DropDownUI,
  ConfirmDialogUI,
  ValidationSummaryDialog,
  ValidationSummaryWatcher,
  hasNestedErrors,
  buildTouchedFromErrors,
} from '../Interface';
import DocumentFields from './DocumentFields';
import { cleanValues, getIcon, getJokerFieldType } from '../../utils/helper';
import documentService from '../../service/newDoc.service';
import DocumentTabs from './DocumentTabs';
import documentTypeService from '../../service/newDocumentType.service';
import appService from '../../service/app.service';
import {
  extractCopyPayload,
  getDocDataGridIdentifiers,
  getSourceDocDataGridIdentifiers,
  getDocumentTypeKeyUpperCase,
} from '../../utils/DocHelper';
import { JokerFieldTypes } from '../../config/joker';
import { useLocation, useNavigate } from 'react-router-dom';
import usePages from '../../context/PageContext';
import { DocumentTypeRoutes, DocumentType } from '../../config/DocumentType';
import {
  DocLineStatus,
  docRelation,
  docSourceRelation,
  DocStatus,
  Operation,
  OperationList,
} from '../../config/document';
import CheckIcon from '@mui/icons-material/Check';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { InvManagementMethod, ItemGroupType } from '../../config/itemGroup';
import BatchDialog from './BatchDialog';
import SourceDocTableDialog from './SourceDocTableDialog';
import SourceDocLineTableDialog from './SourceDocLineTableDialog';
import SettlementDialog from './SettlementDialog';
import useUsers from '../../context/UsersContext';
import NoteDialog from './NoteDialog';
import DownPaymentTableDialog from './DownPaymentTableDialog';
import CompanyDialog from '../Company/CompanyDialog';
import masterDataService from '../../service/masterData.service';
import { DocumentDirection } from '../../config/DocumentDirection';
import BulkUpdateFieldDialog, { defaultBulkUpdateDialogState } from './BulkUpdateFieldDialog';
import ProjectDialog from '../Project/ProjectDialog';
import CostCenterDialog from '../CostCenter/CostCenterDialog';
import DocumentEmailDialog from './DocumentEmailDialog';
import {
  documentValidationSections,
  documentValidationFieldLabels,
} from '../../config/validation/documentValidationConfig';
import useParam from '../../context/ParamContext';
import SupportingDocumentDialog from '../SupportingDocument/SupportingDocumentDialog';

const normalizeSupportingDocumentAssignment = (assignments) => {
  const assignmentKeys = new Set();

  return (assignments ?? []).reduce((result, assignment) => {
    const itemCode = assignment?.itemCode;
    const supportingDocumentTypeId = Number(assignment?.supportingDocumentTypeId);
    const assignmentKey = `${itemCode}-${supportingDocumentTypeId}`;

    if (!itemCode || !Number.isInteger(supportingDocumentTypeId) || assignmentKeys.has(assignmentKey)) {
      return result;
    }

    assignmentKeys.add(assignmentKey);
    result.push({ itemCode, supportingDocumentTypeId });
    return result;
  }, []);
};

const DocumentPage = ({ docState, dialog }) => {
  const {
    setRefetchTable,
    viewField,
    setViewField,
    selectedValues,
    setSelectedValues,
    operatorsDisabled,
    setOperatorsDisabled,
    setRowSelectionModel,
    autocompleteOptions,
    loading,
    autocompleteData,
    datagridIdentifiers,
    setDatagridIdentifiers,
    sourceDatagridIdentifiers,
    setSourceDatagridIdentifiers,
    submenuNames,
    documentData,
    docTypeId,
    calculateBalance,
    setAutoCompleteData,
    setAutoCompleteOptions,
    isLoading,
    upperDocType,
    setUpperDocType,
    jokerFields,
    jokerLineFields,
  } = docState;

  const targetTypeIds = docRelation[docTypeId] || [];
  const sourceTypeIds = docSourceRelation[docTypeId] || [];
  const location = useLocation();
  const navigate = useNavigate();
  const { setPages } = usePages();
  const { config, getConfig } = useConfig();
  const { user } = useUsers();
  const { params } = useParam();
  const supportingDocumentAssignmentEnabled =
    params?.ENABLE_SUPPORTING_DOC === 'Y' &&
    [DocumentType.CustomerOrder, DocumentType.Delivery].includes(parseInt(docTypeId));
  const [initValues, setInitValues] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [canEdit, setCanEdit] = useState(true);
  const [canCopy, setCanCopy] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canEmailSend, setCanEmailSend] = useState(false);
  const [reqDate, setReqDate] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [batchLine, setBatchLine] = useState(null);
  const [binLine, setBinLine] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [documentLangCode, setDocumentLangCode] = useState(null);
  const [hasNoLines, setHasNoLines] = useState(true);
  const [docTableDialogOpen, setDocTableDialogOpen] = useState(false);
  const [docLineTableDialogOpen, setDocLineTableDialogOpen] = useState(false);
  const [docDialogData, setDocDialogData] = useState({ rows: [], rowCount: 0 });
  const [downPaymentDialogData, setDownPaymentDialogData] = useState([]);
  const [downPaymentDialogOpen, setDownPaymentDialogOpen] = useState(false);
  const [selectedDocRows, setSelectedDocRows] = useState({});
  const [docName, setDocName] = useState('');
  const [noteDialogState, setNoteDialogState] = useState({
    open: false,
    valueIndex: null,
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [closeConfirmDialogOpen, setCloseConfirmDialogOpen] = useState(false);
  const [bulkUpdateDialogState, setBulkUpdateDialogState] = useState(defaultBulkUpdateDialogState);
  const [defaultCustomStatus, setDefaultCustomStatus] = useState(null);

  const [settlementDialogOpen, setSettlementDialogOpen] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyDialogSelectedCompanyId, setCompanyDialogSelectedCompanyId] = useState(null);
  const [companyDialogHideRightPanel, setCompanyDialogHideRightPanel] = useState(false);
  const [companyDialogSelectOnClose, setCompanyDialogSelectOnClose] = useState(true);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [costCenterDialogOpen, setCostCenterDialogOpen] = useState(false);
  const [validationDialogState, setValidationDialogState] = useState({
    open: false,
    errors: null,
  });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const [newFiles, setNewFiles] = useState([]);
  const [attachmentEditMode, setAttachmentEditMode] = useState(false);
  const [selectedPrintReportId, setSelectedPrintReportId] = useState(null);
  const [printReportAnchorEl, setPrintReportAnchorEl] = useState(null);
  const [supportingDocumentDialogOpen, setSupportingDocumentDialogOpen] = useState(false);
  const submitLockRef = useRef(false);
  const valuesToFormRunRef = useRef(0);

  const currentDocType = useMemo(
    () => docTypes.find((doc) => doc.documentTypeId === parseInt(docTypeId)),
    [docTypes, docTypeId]
  );
  const availablePrintReports = useMemo(
    () => currentDocType?.printReport ?? documentData?.printReport ?? [],
    [currentDocType, documentData]
  );
  const selectedPrintReport = useMemo(
    () => availablePrintReports.find((report) => Number(report.id) === Number(selectedPrintReportId)) ?? null,
    [availablePrintReports, selectedPrintReportId]
  );
  const hasEmailTemplate = useMemo(
    () => (currentDocType?.template ?? []).some((template) => template?.templateType === 'email'),
    [currentDocType]
  );

  const resolvedDefaultWarehouse = currentDocType?.defaultWarehouse ?? config?.defaultWarehouse ?? null;

  const getDefaultPrintReport = (printReports = []) => {
    return printReports.find((report) => report.docDefault) ?? printReports[0] ?? null;
  };

  const getPrintReportLabel = (printReport) => {
    return [printReport?.label ?? printReport?.name, printReport?.language?.code ?? printReport?.langCode]
      .filter(Boolean)
      .join(' - ');
  };

  const defaultSettlementLine = {
    id: null,
    isNew: true,
    baseType: parseInt(docTypeId),
    baseId: null,
    amount: '',
    currency: '',
    date: dayjs(),
    direction: '',
    mode: '',
  };

  const defaultBatchLine = {
    id: null,
    batchSn: '',
    lot: '',
    supplierId: '',
    expDate: null,
    mnfDate: null,
    batchAttribute1: '',
    batchAttribute2: '',
    comment: '',
    price: '',
    other: '',
    quantity: '',
  };

  const defaultDocumentLine = {
    lineStatus: DocLineStatus.OPENED,
    itemCode: currentDocType?.defaultItemCode ?? null,
    itemName: null,
    drawingNumber: null,
    itemCodeInBp: null,
    itemNameInBp: null,
    qty: currentDocType?.defaultItemCode ? 1 : null,
    cplQty: 0,
    opnQty: currentDocType?.defaultItemCode ? 1 : null,
    reservedQty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: resolvedDefaultWarehouse,
    toWhsCode: null,
    toBinId: null,
    unitPrice: null,
    currencyCode: null,
    vatCode: null,
    vatPercent: null,
    vatValue: null,
    netPrice: null,
    grossPrice: null,
    grossUnitPrice: null,
    ruleId: null,
    discountPercent: null,
    baseUnitPrice: null,
    baseGrossUnitPrice: null,
    dueDate: dayjs(),
    vatValueInOwnCur: null,
    netPriceInOwnCur: null,
    grossPriceInOwnCur: null,
    weightChangeNumber: null,
    weight: null,
    projectId: null,
    projectOrderNumber: '',
    costCenterCode: null,
    itemReference: null,
    internalNote: '',
    bpNote: '',
    isBillable: false,
    finalUnitCost: '',
    finalLineValue: '',
    landedCost: false,
    warranty: false,
    productionPrepared: false,
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_char6: '',
    joker_char7: '',
    joker_char8: '',
    joker_char9: '',
    joker_char10: '',
    joker_date1: null,
    joker_date2: null,
    joker_date3: null,
    joker_date4: null,
    joker_date5: null,
    joker_num1: '',
    joker_num2: '',
    joker_num3: '',
    joker_num4: '',
    joker_num5: '',
    joker_int1: '',
    joker_int2: '',
    joker_int3: '',
    joker_int4: '',
    joker_int5: '',
    docLineBatchSn: [],
    docLineBin: [],
  };

  const defaultValues = {
    docNum: '',
    docId: 0,
    docTypeId: parseInt(docTypeId), // paraméterből gyün
    docStatus: DocStatus.OPENED,
    customStatus: defaultCustomStatus,
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    companyBankId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    employeeId: null,
    projectId: null,
    projectOrderNumber: '',
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: null,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    note: '',
    internalNote: '',
    extraCostTotal: '',
    extraCostNote: '',
    balanceSettled: false,
    settledAmount: '',
    openAmount: '',
    createdBy: user.userId,
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_char6: '',
    joker_char7: '',
    joker_char8: '',
    joker_char9: '',
    joker_char10: '',
    joker_date1: null,
    joker_date2: null,
    joker_date3: null,
    joker_date4: null,
    joker_date5: null,
    joker_num1: '',
    joker_num2: '',
    joker_num3: '',
    joker_num4: '',
    joker_num5: '',
    joker_int1: '',
    joker_int2: '',
    joker_int3: '',
    joker_int4: '',
    joker_int5: '',
    docLine: [],
    settlement: [],
    attachment: [],
    ...(supportingDocumentAssignmentEnabled ? { supportingDocumentAssignment: [] } : {}),
  };

  const normalizeDocLineBatchBin = (line) => {
    const batchRows = line?.docLineBatchSn ?? [];
    const binRows = line?.docLineBin ?? [];

    const refToUiBatchId = new Map();

    const normalizedBatches = batchRows.map((batch) => {
      const batchSnLine = batch.batchSnLine ?? {};
      const batchId = batch.id && typeof batch.id === 'string' ? batch.id : uuidv4();

      const refs = [batch.id, batch.batchSnMovementId, batch.batchTranId, batch.batchSnId, batch.batchSn].filter(
        (v) => v !== null && v !== undefined && v !== ''
      );
      refs.forEach((ref) => refToUiBatchId.set(String(ref), batchId));

      return {
        ...batch,
        id: batchId,
        batchSnId: batch.batchSnId ?? batchSnLine.batchSnId ?? null,
        batchSn: batch.batchSn ?? batchSnLine.batchSn ?? '',
        quantity: batch.quantity,
        lot: batchSnLine.lot ?? batch.lot ?? '',
        supplierId: batchSnLine.supplierId ?? batch.supplierId ?? null,
        expDate: batchSnLine.expDate ?? batch.expDate ? dayjs(batchSnLine.expDate ?? batch.expDate) : null,
        mnfDate: batchSnLine.mnfDate ?? batch.mnfDate ? dayjs(batchSnLine.mnfDate ?? batch.mnfDate) : null,
        batchAttribute1: batchSnLine.batchAttribute1 ?? batch.batchAttribute1 ?? '',
        batchAttribute2: batchSnLine.batchAttribute2 ?? batch.batchAttribute2 ?? '',
        comment: batchSnLine.comment ?? batch.comment ?? '',
        price: batchSnLine.price ?? batch.price ?? '',
        other: batchSnLine.other ?? batch.other ?? '',
      };
    });

    const normalizedBins = binRows.map((bin, index) => {
      const lookupKeys =
        bin.batchTranId !== null && bin.batchTranId !== undefined && bin.batchTranId !== ''
          ? [bin.batchTranId]
          : [bin.snBinId, bin.batchSnId, bin.batchSn].filter((v) => v !== null && v !== undefined && v !== '');

      let linkedBatchId = null;
      for (const key of lookupKeys) {
        const match = refToUiBatchId.get(String(key));
        if (match) {
          linkedBatchId = match;
          break;
        }
      }

      return {
        ...bin,
        id: bin.id ?? `${bin.binId}-${bin.itemCode}-${bin.whsCode}-${index}`,
        binId: bin.binId,
        binCode: bin.binCode,
        snBinId: linkedBatchId,
        quantity: bin.quantity,
      };
    });

    return {
      docLineBatchSn: normalizedBatches,
      docLineBin: normalizedBins,
    };
  };

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
    getDocTypes();
  }, []);

  useEffect(() => {
    const nextDefaultCustomStatus =
      autocompleteData?.customStatus?.find((status) => status.active && status.defaultValue)?.code ?? null;

    setDefaultCustomStatus(nextDefaultCustomStatus);
  }, [autocompleteData.customStatus]);

  useEffect(() => {
    const state = location?.state ?? {};

    const handlePayload = (payload) => payload && Object.keys(payload).length > 0;

    if (handlePayload(state.targetAdd)) {
      setSelectedValues(state.targetAdd);
      setViewField(false);
      setEditButtonvisible(false);
      if (!dialog) navigate(location.pathname, { replace: true });
      return;
    }

    if (handlePayload(state.contactMap)) {
      setSelectedValues(state.contactMap);
      setRowSelectionModel(state.contactMap.docId);
      if (!dialog) navigate(location.pathname, { replace: true });
    }
  }, [location.state]);

  useEffect(() => {
    setOperatorsDisabled(true);

    setDatagridIdentifiers(getDocDataGridIdentifiers(docTypeId));
    if (docTypeId) {
      setUpperDocType(getDocumentTypeKeyUpperCase(docTypeId));
    }
    if (location?.state?.targetAdd === null) {
      setSelectedValues({});
      setViewField(true);
      setEditButtonvisible(true);
    }
  }, [docTypeId]);

  useEffect(() => {
    if (Object.keys(config).length > 0 && !loading) {
      defaultValues.whsCode = resolvedDefaultWarehouse;
      defaultDocumentLine.whsCode = resolvedDefaultWarehouse;
      defaultValues.currencyCode = config.currencyCode;
      setInitValues({ ...defaultValues });
      //setFieldValue('whsCode', config.defaultWarehouse);
    }
  }, [config, loading, resolvedDefaultWarehouse, supportingDocumentAssignmentEnabled]);

  useEffect(() => {
    if (user?.roleId) {
      appService.getDocPermissions(user.roleId).then((data) => {
        setPermissions(data);
      });
    }
  }, [user, docTypeId]);

  useEffect(() => {
    if (permissions.length > 0) {
      const permission = permissions.find((p) => p.documentTypeId === parseInt(docTypeId));

      setCanEdit(!!permission?.roleDocPermissions?.[0]?.canEdit);
      setCanCopy(!!permission?.roleDocPermissions?.[0]?.canCopy);
      setCanDelete(!!permission?.roleDocPermissions?.[0]?.canDelete);
      setCanEmailSend(!!permission?.roleDocPermissions?.[0]?.canEmailSend);
    }
  }, [permissions, docTypeId]);

  useEffect(() => {
    if (availablePrintReports.length === 0) {
      setSelectedPrintReportId(null);
      return;
    }

    if (
      selectedPrintReportId &&
      availablePrintReports.some((report) => Number(report.id) === Number(selectedPrintReportId))
    ) {
      return;
    }

    setSelectedPrintReportId(getDefaultPrintReport(availablePrintReports)?.id ?? null);
  }, [availablePrintReports, selectedPrintReportId]);

  useEffect(() => {
    let isMounted = true;

    const resolvePrintReport = async () => {
      if (!docTypeId || availablePrintReports.length === 0) {
        if (isMounted) {
          setSelectedPrintReportId(null);
        }
        return;
      }

      if (!companyId) {
        if (isMounted) {
          setSelectedPrintReportId(getDefaultPrintReport(availablePrintReports)?.id ?? null);
        }
        return;
      }

      try {
        const resolvedReport = await documentTypeService.resolvePrintReport(
          docTypeId,
          companyId,
          documentLangCode ?? undefined
        );
        const fallbackReportId = getDefaultPrintReport(availablePrintReports)?.id ?? null;
        const nextReportId = availablePrintReports.some((report) => Number(report.id) === Number(resolvedReport?.id))
          ? resolvedReport?.id
          : fallbackReportId;

        if (isMounted) {
          setSelectedPrintReportId(nextReportId);
        }
      } catch {
        if (isMounted) {
          setSelectedPrintReportId(getDefaultPrintReport(availablePrintReports)?.id ?? null);
        }
      }
    };

    resolvePrintReport();

    return () => {
      isMounted = false;
    };
  }, [docTypeId, companyId, documentLangCode, availablePrintReports]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      const currentRun = ++valuesToFormRunRef.current;
      if (selectedValues) {
        valuesToForm(selectedValues, currentRun);
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      valuesToFormRunRef.current += 1;
      setOperatorsDisabled(true);

      if (config?.currencyCode && !loading) {
        defaultValues.whsCode = resolvedDefaultWarehouse;
        defaultValues.currencyCode = config.currencyCode;
      }

      if (config?.defPriceList) defaultValues.priceListId = config.defPriceList;
      if (config?.defPaymentMethod) defaultValues.paymentMethodId = config.defPaymentMethod;

      setInitValues({ ...defaultValues });
    }
  }, [
    selectedValues,
    config,
    loading,
    autocompleteData.customStatus,
    resolvedDefaultWarehouse,
    supportingDocumentAssignmentEnabled,
  ]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0 && parseInt(docTypeId) === DocumentType.DownPaymentRequest) {
      hasTargetDownPaymentRequest();
    }
  }, [selectedValues.docId]);

  useEffect(() => {
    setAttachmentEditMode(false);
  }, [selectedValues.docId]);

  useEffect(() => {
    checkReferenced();
  }, [selectedValues.docId]);

  const normalizeSingleItemResponse = (response) => {
    if (Array.isArray(response?.body)) {
      return response.body[0] ?? null;
    }

    if (Array.isArray(response)) {
      return response[0] ?? null;
    }

    return response?.body ?? response ?? null;
  };

  const ensureItemInAutocompleteCache = (item) => {
    if (!item?.itemCode) return item;

    setAutoCompleteData((prev) => {
      const prevItems = Array.isArray(prev?.item) ? prev.item : [];
      const nextItems = prevItems.some((i) => i.itemCode === item.itemCode)
        ? prevItems.map((i) => (i.itemCode === item.itemCode ? item : i))
        : [...prevItems, item];

      return {
        ...prev,
        item: nextItems,
      };
    });

    setAutoCompleteOptions((prev) => {
      const prevItems = Array.isArray(prev?.item) ? prev.item : [];
      const nextOption = { label: [item.itemCode, item.itemName].filter(Boolean).join(' - '), value: item.itemCode };
      const nextItems = prevItems.some((i) => i.value === item.itemCode)
        ? prevItems.map((i) => (i.value === item.itemCode ? nextOption : i))
        : [...prevItems, nextOption];

      return {
        ...prev,
        item: nextItems,
      };
    });

    return item;
  };

  const getItemMetaByCode = async (itemCode) => {
    if (!itemCode) return null;

    const cachedItem = autocompleteData?.item?.find((item) => item.itemCode === itemCode);
    if (cachedItem) {
      return cachedItem;
    }

    try {
      const response = await masterDataService.itemsFromTableToFormByItemCode(itemCode);
      const item = normalizeSingleItemResponse(response);
      if (item) {
        ensureItemInAutocompleteCache(item);
      }
      return item;
    } catch {
      return null;
    }
  };

  const ensureItemsInAutocompleteCache = async (itemCodes) => {
    const uniqueCodes = [...new Set((itemCodes ?? []).filter(Boolean))];
    if (uniqueCodes.length === 0) return [];

    return Promise.all(uniqueCodes.map((itemCode) => getItemMetaByCode(itemCode)));
  };

  const getDocTypes = () => {
    documentTypeService.allDocumentTypes().then((data) => {
      setDocTypes(data);
    });
  };

  const valuesToForm = async (sourceValues = selectedValues, runId = valuesToFormRunRef.current) => {
    const {
      docId,
      docNum,
      docTypeId,
      docStatus,
      customStatus,
      stornoId,
      docDate,
      issueDate,
      dueDate,
      companyId,
      companyCode,
      companyName,
      companyContactId,
      companyBankId,
      shortName,
      incotermCity,
      companyWarning,
      currencyCode,
      exchangeRate,
      paymentMethodId,
      reference,
      employeeId,
      projectId,
      costCenterCode,
      salesOrPurchasePersonId,
      billingAdressId,
      billingCountry,
      billingProvince,
      billingPostCode,
      billingCity,
      billingStreetAddress,
      shippingAdressId,
      shippingCountry,
      shippingProvince,
      shippingPostCode,
      shippingCity,
      shippingStreetAddress,
      deliveryMethodId,
      incotermCode,
      whsCode,
      toWhsCode,
      priceListId,
      langCode,
      hunTaxNumber,
      euTaxNumber,
      foreignTaxNumber,
      sumInDocCur,
      netInDocCur,
      VATInDocCur,
      roundingInDocCur,
      sumInOwnCur,
      netInOwnCur,
      VATInOwnCur,
      note,
      internalNote,
      extraCostTotal,
      extraCostNote,
      balanceSettled,
      settledAmount,
      openAmount,
      createdBy,
      docLine,
      joker_char1,
      joker_char2,
      joker_char3,
      joker_char4,
      joker_char5,
      joker_char6,
      joker_char7,
      joker_char8,
      joker_char9,
      joker_char10,
      joker_date1,
      joker_date2,
      joker_date3,
      joker_date4,
      joker_date5,
      joker_num1,
      joker_num2,
      joker_num3,
      joker_num4,
      joker_num5,
      joker_int1,
      joker_int2,
      joker_int3,
      joker_int4,
      joker_int5,
      settlement,
      attachment,
      supportingDocumentAssignment,
    } = sourceValues;

    await ensureItemsInAutocompleteCache(docLine.map((line) => line?.itemCode));

    if (runId !== valuesToFormRunRef.current) return;

    const isCopyMode = sourceValues?.mode === 'copy';

    setInitValues({
      docId,
      docNum: docNum ?? '',
      docTypeId,
      docStatus: isCopyMode ? DocStatus.OPENED : docStatus,
      customStatus: isCopyMode ? defaultCustomStatus ?? null : customStatus ?? null,
      stornoId,
      docDate: dayjs(docDate),
      issueDate: dayjs(issueDate),
      dueDate: dayjs(dueDate),
      companyId,
      companyCode: companyCode ?? '',
      companyName: companyName ?? '',
      companyContactId,
      companyBankId,
      shortName,
      incotermCity,
      companyWarning,
      currencyCode,
      exchangeRate,
      paymentMethodId,
      reference: reference ?? '',
      employeeId,
      projectId,
      costCenterCode,
      salesOrPurchasePersonId,
      billingAdressId,
      billingCountry: billingCountry ?? '',
      billingProvince: billingProvince ?? '',
      billingPostCode: billingPostCode ?? '',
      billingCity: billingCity ?? '',
      billingStreetAddress: billingStreetAddress ?? '',
      shippingAdressId,
      shippingCountry: shippingCountry ?? '',
      shippingProvince: shippingProvince ?? '',
      shippingPostCode: shippingPostCode ?? '',
      shippingCity: shippingCity ?? '',
      shippingStreetAddress: shippingStreetAddress ?? '',
      deliveryMethodId,
      incotermCode,
      whsCode,
      toWhsCode,
      priceListId,
      langCode,
      hunTaxNumber: hunTaxNumber ?? '',
      euTaxNumber: euTaxNumber ?? '',
      foreignTaxNumber: foreignTaxNumber ?? '',
      sumInDocCur,
      netInDocCur,
      VATInDocCur,
      roundingInDocCur,
      sumInOwnCur,
      netInOwnCur,
      VATInOwnCur,
      note: note ?? '',
      internalNote: internalNote ?? '',
      extraCostTotal: extraCostTotal ?? '',
      extraCostNote: extraCostNote ?? '',
      balanceSettled: balanceSettled ?? false,
      settledAmount: settledAmount ?? '',
      openAmount: openAmount ?? '',
      createdBy,
      joker_char1: joker_char1 ?? '',
      joker_char2: joker_char2 ?? '',
      joker_char3: joker_char3 ?? '',
      joker_char4: joker_char4 ?? '',
      joker_char5: joker_char5 ?? '',
      joker_char6: joker_char6 ?? '',
      joker_char7: joker_char7 ?? '',
      joker_char8: joker_char8 ?? '',
      joker_char9: joker_char9 ?? '',
      joker_char10: joker_char10 ?? '',
      joker_date1,
      joker_date2,
      joker_date3,
      joker_date4,
      joker_date5,
      joker_num1: joker_num1 ?? '',
      joker_num2: joker_num2 ?? '',
      joker_num3: joker_num3 ?? '',
      joker_num4: joker_num4 ?? '',
      joker_num5: joker_num5 ?? '',
      joker_int1: joker_int1 ?? '',
      joker_int2: joker_int2 ?? '',
      joker_int3: joker_int3 ?? '',
      joker_int4: joker_int4 ?? '',
      joker_int5: joker_int5 ?? '',
      docLine: docLine?.map((dl) => {
        const { docLineBatchSn: normalizedBatches, docLineBin: normalizedBins } = normalizeDocLineBatchBin(dl);

        return {
          id: uuidv4(),
          isNew: sourceValues?.mode === 'copy' ? true : Boolean(dl.isNew),
          docId: dl.docId,
          lineNum: dl.lineNum,
          lineStatus: isCopyMode ? DocLineStatus.OPENED : dl.lineStatus,
          itemCode: dl.itemCode,
          itemName: dl.itemName,
          drawingNumber: dl.drawingNumber ?? null,
          itemCodeInBp: dl.itemCodeInBp,
          itemNameInBp: dl.itemNameInBp,
          qty: dl.qty,
          cplQty: dl.cplQty,
          opnQty: dl.opnQty,
          reservedQty: dl.reservedQty ?? null,
          quantityUnitId: dl.quantityUnitId,
          quantityUnitCode: dl.quantityUnitCode,
          quantityUnitName: dl.quantityUnitName,
          whsCode: dl.whsCode,
          toWhsCode: dl.toWhsCode,
          toBinId: dl.toBinId,
          unitPrice: dl.unitPrice,
          currencyCode: dl.currencyCode,
          vatCode: dl.vatCode,
          vatPercent: dl.vatPercent,
          vatValue: dl.vatValue,
          netPrice: dl.netPrice,
          grossPrice: dl.grossPrice,
          grossUnitPrice: dl.grossUnitPrice,
          ruleId: dl.ruleId,
          discountPercent: dl.discountPercent,
          baseUnitPrice: dl.baseUnitPrice,
          baseGrossUnitPrice: dl.baseGrossUnitPrice,
          dueDate: dayjs(dl.dueDate),
          vatValueInOwnCur: dl.vatValueInOwnCur,
          netPriceInOwnCur: dl.netPriceInOwnCur,
          grossPriceInOwnCur: dl.grossPriceInOwnCur,
          weightChangeNumber: dl.weightChangeNumber,
          weight: dl.weight,
          projectId: dl.projectId,
          projectOrderNumber: dl.project?.orderNumber ?? '',
          costCenterCode: dl.costCenterCode,
          itemReference: dl.itemReference,
          internalNote: dl.internalNote,
          bpNote: dl.bpNote,
          isBillable: dl.isBillable ?? false,
          finalUnitCost: dl.finalUnitCost ?? '',
          finalLineValue: dl.finalLineValue ?? '',
          landedCost: dl.landedCost ?? false,
          warranty: dl.warranty ?? false,
          productionPrepared: dl.productionPrepared ?? false,
          joker_char1: dl.joker_char1 ?? '',
          joker_char2: dl.joker_char2 ?? '',
          joker_char3: dl.joker_char3 ?? '',
          joker_char4: dl.joker_char4 ?? '',
          joker_char5: dl.joker_char5 ?? '',
          joker_char6: dl.joker_char6 ?? '',
          joker_char7: dl.joker_char7 ?? '',
          joker_char8: dl.joker_char8 ?? '',
          joker_char9: dl.joker_char9 ?? '',
          joker_char10: dl.joker_char10 ?? '',
          joker_date1: dl.joker_date1,
          joker_date2: dl.joker_date2,
          joker_date3: dl.joker_date3,
          joker_date4: dl.joker_date4,
          joker_date5: dl.joker_date5,
          joker_num1: dl.joker_num1 ?? '',
          joker_num2: dl.joker_num2 ?? '',
          joker_num3: dl.joker_num3 ?? '',
          joker_num4: dl.joker_num4 ?? '',
          joker_num5: dl.joker_num5 ?? '',
          joker_int1: dl.joker_int1 ?? '',
          joker_int2: dl.joker_int2 ?? '',
          joker_int3: dl.joker_int3 ?? '',
          joker_int4: dl.joker_int4 ?? '',
          joker_int5: dl.joker_int5 ?? '',
          baseId: location?.state?.targetAdd !== undefined ? sourceValues.baseId : dl.baseId,
          baseDocnum: location?.state?.targetAdd !== undefined ? sourceValues.baseDocnum : dl.baseDocnum,
          baseDocType: location?.state?.targetAdd !== undefined ? sourceValues.baseDocType : dl.baseDocType,
          baseLine: location?.state?.targetAdd !== undefined ? sourceValues.baseLine : dl.baseLine,
          deletable: dl.deletable,
          docLineBatchSn: normalizedBatches,
          docLineBin: normalizedBins,
        };
      }),
      settlement: settlement?.map((s) => ({
        id: s.id,
        baseType: s.baseType,
        baseId: s.baseId,
        amount: s.amount,
        currency: s.currency,
        date: dayjs(s.date),
        direction: s.direction,
        mode: s.mode,
        isNew: false,
      })),
      attachment,
      ...(supportingDocumentAssignmentEnabled &&
      Object.prototype.hasOwnProperty.call(sourceValues, 'supportingDocumentAssignment')
        ? {
            supportingDocumentAssignment: normalizeSupportingDocumentAssignment(supportingDocumentAssignment),
          }
        : {}),
    });

    setNewFiles([]);
  };

  const validationSchema = Yup.object().shape({
    docNum: currentDocType?.manualDocNum ? Yup.string().trim().required() : Yup.string().nullable(),
    docStatus: Yup.string().required(),
    customStatus: Yup.string().nullable(),
    stornoId: Yup.string().nullable(),
    docDate: Yup.string().required(),
    issueDate: Yup.string().required(),
    dueDate: Yup.string().required(),
    companyId: Yup.string().nullable(),
    companyCode: Yup.string().nullable(),
    companyName: Yup.string().nullable(),
    companyContactId: Yup.string().nullable(),
    companyBankId: Yup.string().nullable(),
    shortName: Yup.string().nullable(),
    incotermCity: Yup.string().nullable(),
    companyWarning: Yup.string().nullable(),
    currencyCode: Yup.string().nullable(),
    exchangeRate: Yup.number()
      .nullable()
      .when('currencyCode', {
        is: (currencyCode) => config?.currencyCode !== currencyCode,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      }),
    paymentMethodId: Yup.string().nullable(),
    reference: Yup.string().nullable(),
    employeeId: Yup.string().nullable(),
    projectId: Yup.string().nullable(),
    projectOrderNumber: Yup.string().nullable(),
    costCenterCode: Yup.string().nullable(),
    salesOrPurchasePersonId: Yup.string().nullable(),
    billingAdressId: Yup.string().nullable(),
    billingCountry: Yup.string().nullable(),
    billingProvince: Yup.string().nullable(),
    billingPostCode: Yup.string().nullable(),
    billingCity: Yup.string().nullable(),
    billingStreetAddress: Yup.string().nullable(),
    shippingAdressId: Yup.string().nullable(),
    shippingCountry: Yup.string().nullable(),
    shippingProvince: Yup.string().nullable(),
    shippingPostCode: Yup.string().nullable(),
    shippingCity: Yup.string().nullable(),
    shippingStreetAddress: Yup.string().nullable(),
    deliveryMethodId: Yup.string().nullable(),
    incotermCode: Yup.string().nullable(),
    whsCode: Yup.string().nullable(),
    toWhsCode: Yup.string().nullable(),
    priceListId: Yup.string().nullable(),
    langCode: Yup.string().nullable(),
    hunTaxNumber: Yup.string()
      .nullable()
      .matches(/^\d{8}(?:-\d{1,2}-\d{2})?$/, {
        message: 'A magyar adószám formátuma helytelen. Helyes formátum például: 12345678 vagy 12345678-1-23',
        excludeEmptyString: true,
      }),
    euTaxNumber: Yup.string()
      .nullable()
      .matches(/^[A-Z]{2}[A-Z0-9]{2,18}$/, {
        message: 'Az EU adószám formátuma helytelen. Helyes formátum például: HU12345678',
        excludeEmptyString: true,
      }),
    foreignTaxNumber: Yup.string().nullable(),
    note: Yup.string().nullable().max(3000),
    internalNote: Yup.string().nullable(),
    extraCostTotal: Yup.string().nullable(),
    extraCostNote: Yup.string().nullable(),
    balanceSettled: Yup.boolean().nullable(),
    settledAmount: Yup.string().nullable(),
    openAmount: Yup.string().nullable(),
    docLine: Yup.array().of(
      Yup.object().shape({
        lineStatus: Yup.string().required(),
        itemCode: Yup.string().required(),
        itemName: Yup.string().nullable(),
        drawingNumber: Yup.string().nullable(),
        itemCodeInBp: Yup.string().nullable(),
        itemNameInBp: Yup.string().nullable(),
        qty: Yup.number().positive().required(),
        reservedQty: Yup.number().nullable(),
        quantityUnitId: Yup.string().nullable(),
        whsCode: Yup.string().nullable(),
        toWhsCode: Yup.string().nullable(),
        toBinId: Yup.string().nullable(),
        unitPrice: Yup.string().nullable(),
        currencyCode: Yup.string().nullable(),
        vatCode: Yup.string().nullable(),
        vatPercent: Yup.string().nullable(),
        vatValue: Yup.string().nullable(),
        netPrice: Yup.string().nullable(),
        grossPrice: Yup.string().nullable(),
        grossUnitPrice: Yup.string().nullable(),
        ruleId: Yup.string().nullable(),
        discountPercent: Yup.string().nullable(),
        baseUnitPrice: Yup.string().nullable(),
        baseGrossUnitPrice: Yup.string().nullable(),
        dueDate: Yup.string().required(),
        vatValueInOwnCur: Yup.string().nullable(),
        netPriceInOwnCur: Yup.string().nullable(),
        grossPriceInOwnCur: Yup.string().nullable(),
        weightChangeNumber: Yup.string().nullable(),
        weight: Yup.string().nullable(),
        projectId: Yup.string().nullable(),
        projectOrderNumber: Yup.string().nullable(),
        costCenterCode: Yup.string().nullable(),
        itemReference: Yup.string().nullable(),
        internalNote: Yup.string().nullable(),
        bpNote: Yup.string().nullable().max(3000),
        isBillable: Yup.boolean().nullable(),
        finalUnitCost: Yup.string().nullable(),
        finalLineValue: Yup.string().nullable(),
        landedCost: Yup.boolean().nullable(),
        warranty: Yup.boolean().nullable(),
        productionPrepared: Yup.boolean().nullable(),
        // Joker mezők shape-be illesztése
        ...(jokerLineFields.length > 0
          ? Object.fromEntries(
              jokerLineFields.map((field) => {
                const fieldType = getJokerFieldType(field);
                return [
                  field.field,
                  fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
                ];
              })
            )
          : {}),
        docLineBatchSn: Yup.array().of(
          Yup.object().shape({
            batchSn: Yup.string().required(),
            binCode: Yup.string().nullable(),
            binId: Yup.number().nullable(),
            lot: Yup.string().nullable(),
            supplierId: Yup.string().nullable(),
            expDate: Yup.string().nullable(),
            mnfDate: Yup.string().nullable(),
            batchAttribute1: Yup.string().nullable(),
            batchAttribute2: Yup.string().nullable(),
            comment: Yup.string().nullable(),
            price: Yup.string().nullable(),
            other: Yup.string().nullable(),
            quantity: Yup.number().required(),
          })
        ),
        docLineBin: Yup.array().of(
          Yup.object().shape({
            binCode: Yup.string().required(),
            binId: Yup.number().nullable(),
            quantity: Yup.number().required(),
          })
        ),
      })
    ),
    settlement: Yup.array().of(
      Yup.object().shape({
        amount: Yup.number().required(),
        date: Yup.string().required(),
        mode: Yup.string().required(),
      })
    ),
    attachment: Yup.array().of(
      Yup.object().shape({
        id: Yup.number().nullable(),
        name: Yup.string().nullable(),
        path: Yup.string().nullable(),
        extension: Yup.string().nullable(),
        docId: Yup.number().nullable(),
        external: Yup.boolean().nullable(),
      })
    ),

    // Joker mezők shape-be illesztése
    ...(jokerFields.length > 0
      ? Object.fromEntries(
          jokerFields.map((field) => {
            const fieldType = getJokerFieldType(field);
            return [
              field.field,
              fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
            ];
          })
        )
      : {}),
  });

  const checkItemType = async (values) => {
    for (const docLine of values.docLine) {
      if (!docLine.isNew || !currentDocType.inventoryAccounting) continue;

      const matchedItem = await getItemMetaByCode(docLine.itemCode);
      if (matchedItem && matchedItem.type !== ItemGroupType.SERVICE) {
        return true;
      }
    }

    return false;
  };

  const checkBatchQty = async (values) => {
    if (!documentData.inventoryAccounting) return false;
    for (const line of values.docLine) {
      const item = await getItemMetaByCode(line.itemCode);
      if (!item) continue;
      const whsCode = line.whsCode ?? values.whsCode;
      const whs = autocompleteData.warehouse.find((w) => w.code === whsCode);
      const isBinManaged = Boolean(whs?.binManaged);

      if (item.invManagementMethod === InvManagementMethod.BATCH) {
        const totalBatchQty = line.docLineBatchSn.reduce(
          (total, batch) => add(parseFloat(total), parseFloat(batch.quantity || 0)),
          0
        );
        if (totalBatchQty !== parseFloat(line.qty)) {
          addNotification({
            content: `A(z) ${line.itemCode} cikkhez tartozó sarzsok mennyisége (${totalBatchQty}) nem egyezik meg a sor mennyiségével (${line.qty})!`,
            type: NotificationType.ERROR,
          });
          return true;
        }

        if (isBinManaged) {
          for (const batch of line.docLineBatchSn) {
            if (!batch.batchSn) continue;
            const bin = (line.docLineBin ?? []).find((b) => b.snBinId === batch.id);
            if (!bin?.binCode) {
              addNotification({
                content: `A(z) ${line.itemCode} cikkhez tartozó sarzsoknál kötelező a rakhely megadása!`,
                type: NotificationType.ERROR,
              });
              return true;
            }

            const binQty = parseFloat(bin.quantity ?? 0) || 0;
            const batchQty = parseFloat(batch.quantity ?? 0) || 0;
            if (!binQty || (batchQty && binQty !== batchQty)) {
              addNotification({
                content: `A(z) ${line.itemCode} cikkhez tartozó rakhely mennyisége nem egyezik a sarzs mennyiségével!`,
                type: NotificationType.ERROR,
              });
              return true;
            }
          }
        }

        continue;
      }

      if (isBinManaged && item.type !== ItemGroupType.SERVICE) {
        const bins = line.docLineBin ?? [];
        if (!bins.length) {
          addNotification({
            content: `A(z) ${line.itemCode} cikkhez rakhely megadása kötelező!`,
            type: NotificationType.ERROR,
          });
          return true;
        }

        const incomplete = bins.some((bin) => !bin.binCode || !Number(bin.quantity));
        if (incomplete) {
          addNotification({
            content: `A(z) ${line.itemCode} cikkhez rakhely és mennyiség megadása kötelező!`,
            type: NotificationType.ERROR,
          });
          return true;
        }

        const totalBinQty = bins.reduce((total, bin) => add(parseFloat(total), parseFloat(bin.quantity || 0)), 0);
        if (totalBinQty !== parseFloat(line.qty)) {
          addNotification({
            content: `A(z) ${line.itemCode} cikkhez tartozó rakhelyek mennyisége (${totalBinQty}) nem egyezik meg a sor mennyiségével (${line.qty})!`,
            type: NotificationType.ERROR,
          });
          return true;
        }
      }
    }

    for (const line of values.docLine) {
      const item = await getItemMetaByCode(line.itemCode);
      if (!item || item.type === ItemGroupType.SERVICE) continue;

      const totalBinQty = (line.docLineBin ?? []).reduce(
        (total, bin) => add(parseFloat(total), parseFloat(bin.quantity || 0)),
        0
      );
      if (totalBinQty > parseFloat(line.qty)) {
        addNotification({
          content: `A(z) ${line.itemCode} cikkhez tartozó rakhelyek mennyisége (${totalBinQty}) nem lehet több, mint a sor mennyisége (${line.qty})!`,
          type: NotificationType.ERROR,
        });
        return true;
      }
    }

    return false;
  };

  const insertObject = async (values, resetForm) => {
    const bathError = await checkBatchQty(values);
    if (bathError) return false;
    const cleanedValues = cleanValues(values);
    delete cleanedValues.supportingDocumentAssignment;

    const insertObject = {
      ...cleanedValues,
      ...(supportingDocumentAssignmentEnabled && Array.isArray(values.supportingDocumentAssignment)
        ? {
            supportingDocumentAssignment: normalizeSupportingDocumentAssignment(
              values.supportingDocumentAssignment
            ),
          }
        : {}),
      docLine: values.docLine.map((dl) => ({
        ...cleanValues(dl),
        docLineBatchSn: dl.docLineBatchSn.map((batch) => ({
          ...cleanValues(batch),
          quantity: parseFloat(batch.quantity || 0),
        })),
        docLineBin: (dl.docLineBin ?? []).map((bin) => ({
          ...cleanValues(bin),
          quantity: parseFloat(bin.quantity || 0),
        })),
      })),
      createdBy: user.userId,
      docDate: dayjs(values.docDate).startOf('day').add(12, 'hour').toISOString(),
      issueDate: dayjs(values.issueDate).startOf('day').add(12, 'hour').toISOString(),
      dueDate: dayjs(values.dueDate).startOf('day').add(12, 'hour').toISOString(),
    };

    const data = await documentService.createDocument(insertObject, newFiles);
    addNotification({
      content: 'Sikeres mentés!',
      type: NotificationType.SUCCESS,
    });
    setRefetchTable(true);
    setAddField(true);
    setViewField(true);
    setAttachmentEditMode(false);
    setSelectedValues(data);
    setRowSelectionModel([data.docId]);
    if (resetForm) resetForm();
    setNewFiles([]);

    return true;
  };

  const updateObject = async (values, resetForm) => {
    if (values.docStatus !== DocStatus.PLANNED) {
      if (await checkItemType(values)) {
        addNotification({
          content: 'Csak olyan sor adható hozzá, ahol a cikk típusa szolgálatatás!',
          type: NotificationType.ERROR,
        });
        return false;
      }
    }

    const bathError = await checkBatchQty(values);
    if (bathError) return false;

    const cleanedValues = cleanValues(values);
    delete cleanedValues.supportingDocumentAssignment;

    const updData = {
      ...cleanedValues,
      ...(supportingDocumentAssignmentEnabled && Array.isArray(values.supportingDocumentAssignment)
        ? {
            supportingDocumentAssignment: normalizeSupportingDocumentAssignment(
              values.supportingDocumentAssignment
            ),
          }
        : {}),
      docLine: values.docLine.map((dl) => ({
        ...cleanValues(dl),
        dueDate: dl.dueDate ? dayjs(dl.dueDate).startOf('day').add(12, 'hour').toISOString() : null,
        docLineBatchSn: dl.docLineBatchSn.map((batch) => ({
          ...cleanValues(batch),
          quantity: parseFloat(batch.quantity || 0),
        })),
        docLineBin: (dl.docLineBin ?? []).map((bin) => ({
          ...cleanValues(bin),
          quantity: parseFloat(bin.quantity || 0),
        })),
      })),
      updatedBy: user.userId,
      docDate: dayjs(values.docDate).startOf('day').add(12, 'hour').toISOString(),
      issueDate: dayjs(values.issueDate).startOf('day').add(12, 'hour').toISOString(),
      dueDate: dayjs(values.dueDate).startOf('day').add(12, 'hour').toISOString(),
      reqDate: reqDate ? dayjs(reqDate).toISOString() : dayjs().toISOString(),
    };

    const data = await documentService.updateDocument(updData, selectedValues.docId, newFiles);
    addNotification({
      content: 'Sikeres módosítás!',
      type: NotificationType.SUCCESS,
    });
    setRefetchTable(true);
    setEditButtonvisible(true);
    setViewField(true);
    setAttachmentEditMode(false);
    setSelectedValues(data);
    if (resetForm) resetForm();
    setNewFiles([]);

    return true;
  };

  const calculeteAdditionalCost = (values) => {
    const lines = Array.isArray(values.docLine) ? values.docLine : [];
    if (lines.length === 0) {
      return values;
    }

    // fejléci extra költség saját pénznemben
    const headerExtraCostInOwnCur = parseFloat(values.extraCostTotal ?? 0) || 0;

    // landed cost sorok nettó értékének összege (saját pénznem)
    const landedCostTotalInOwnCur = lines.reduce((sum, line) => {
      if (!line.landedCost) return sum;
      const net = parseFloat(line.netPriceInOwnCur ?? 0);
      return add(sum, isNaN(net) ? 0 : net);
    }, 0);

    // ÖSSZES LEOSZTANDÓ KÖLTSÉG = fejléci extra + landed cost sorok nettója
    const totalExtraCostInOwnCur = add(headerExtraCostInOwnCur, landedCostTotalInOwnCur);

    // csak készletes (STOCKARTICLE) és nem landed cost sorok legyenek az elosztási körben
    const stockLines = lines.filter((line) => {
      const item = autocompleteData.item?.find((i) => i.itemCode === line.itemCode);
      return item && item.type === ItemGroupType.STOCKARTICLE && !line.landedCost;
    });

    if (stockLines.length === 0) {
      return values;
    }

    // SUM(p.netPriceInOwnCur) az elosztási körön belül
    const totalBaseValue = stockLines.reduce((sum, line) => {
      const net = parseFloat(line.netPriceInOwnCur ?? 0);
      return add(sum, isNaN(net) ? 0 : net);
    }, 0);

    if (totalBaseValue === 0) {
      return values;
    }

    const stockLineIds = new Set(stockLines.map((l) => l.id));
    const hasBase = totalBaseValue !== 0;

    const mappedLines = lines.map((line) => {
      // csak a készletes, nem landed cost sorokra számoljuk a final* értékeket
      if (!stockLineIds.has(line.id)) {
        return line;
      }

      const qty = parseFloat(line.qty ?? 0) || 0;
      const netPriceInOwnCur = parseFloat(line.netPriceInOwnCur ?? 0) || 0;
      const unitPrice = parseFloat(line.unitPrice ?? 0) || 0;
      const exchangeRate = parseFloat(values.exchangeRate ?? 1) || 1;

      // p.unitPrice * IFNULL(p2.exchangeRate, 1)
      const unitPriceInOwnCur = add(unitPrice * exchangeRate, 0);

      // ratio = p.netPriceInOwnCur / totalBaseValue (0 ha totalBaseValue = 0)
      const ratio = hasBase ? netPriceInOwnCur / totalBaseValue : 0;

      // sorra eső elosztott költség (NEM kerekítve, csak számítás)
      const allocatedExtraCost = totalExtraCostInOwnCur * ratio;

      // egységre jutó extra költség (NEM kerekítve)
      const extraCostPerUnit = qty !== 0 ? allocatedExtraCost / qty : 0;

      // végső egységár (finalUnitCost) – kerekítve
      const finalUnitCost = Math.round(unitPriceInOwnCur + extraCostPerUnit);

      // végső sorérték (finalLineValue) – kerekítve
      const finalLineValue = Math.round(netPriceInOwnCur + allocatedExtraCost);

      return {
        ...line,
        finalUnitCost,
        finalLineValue,
      };
    });

    return {
      ...values,
      docLine: mappedLines,
    };
  };

  const handleSubmit = async (rawValues, helpers, isDraft) => {
    if (submitLockRef.current || helpers.isSubmitting) return;
    submitLockRef.current = true;

    let values = { ...rawValues };
    helpers.setSubmitting(true);

    try {
      if (isDraft) {
        values.docStatus = DocStatus.PLANNED;
      }
      if (documentData?.additionalCost && selectedValues?.mode !== 'copy') {
        values = calculeteAdditionalCost(values);
      }

      if (selectedValues?.mode === 'copy') {
        const copyValues = {
          ...values,
          balanceSettled: false,
          settledAmount: '',
          openAmount: '',
          settlement: [],
        };

        await insertObject(copyValues, helpers.resetForm);
      } else if (!editButtonVisible) {
        if (!isDraft && values.docStatus === DocStatus.PLANNED) {
          values.docStatus = DocStatus.OPENED;
        }

        await updateObject(values, helpers.resetForm);
      } else if (!addField) {
        await insertObject(values, helpers.resetForm);
      }
    } finally {
      helpers.setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    documentService.deleteDocument(deleteData, selectedValues.docId, docTypeId).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
    });
  };

  const printClick = () => {
    const printReportId = selectedPrintReport?.id ?? getDefaultPrintReport(availablePrintReports)?.id;

    if (!printReportId) {
      return addNotification({
        content: 'Nem áll rendelkezésre nyomtatási kép!',
        type: NotificationType.WARNING,
      });
    }

    addNotification({
      content: 'Nyomtatás elindult!',
      type: NotificationType.INFO,
    });

    documentService.printDocument({
      docId: selectedValues.docId,
      documentTypeId: selectedValues.docTypeId,
      printReportId,
    });
  };

  const handleSendDocumentEmail = async (emailData) => {
    const { files = [], ...data } = emailData;

    await documentService.sendEmail(data, files);

    addNotification({
      content: 'Az e-mail küldés elindult!',
      type: NotificationType.SUCCESS,
    });

    setEmailDialogOpen(false);
  };

  const handleOpenPrintReportMenu = (event) => {
    setPrintReportAnchorEl(event.currentTarget);
  };

  const handleClosePrintReportMenu = () => {
    setPrintReportAnchorEl(null);
  };

  const handleSelectPrintReport = (printReportId) => {
    setSelectedPrintReportId(printReportId);
    handleClosePrintReportMenu();
  };

  const options = docTypes
    .filter((doc) => {
      const permission = permissions.find((p) => p.documentTypeId === parseInt(doc.documentTypeId));
      const canEdit = !!permission?.roleDocPermissions?.[0]?.canEdit;

      return targetTypeIds.includes(doc.documentTypeId) && canEdit;
    })
    .map((doc) => ({ label: doc.name, value: doc.documentTypeId }));

  const sourceOptions = docTypes
    .filter((doc) => sourceTypeIds.includes(doc.documentTypeId))
    .map((doc) => ({ label: doc.name, value: doc.documentTypeId }));

  const onSelect = async (value) => {
    const selectedDocType = docTypes.find((doc) => doc.documentTypeId === parseInt(value));
    const selectedItem = await getItemMetaByCode(selectedDocType?.defaultItemCode);
    const findedQtyUnit = autocompleteData.qtyUnit.find((u) => u.id === selectedItem?.quantityUnitId);

    const defVals = {
      ...defaultDocumentLine,
      itemCode: selectedDocType?.defaultItemCode,
      itemName: selectedItem?.itemName,
      quantityUnitId: selectedItem?.quantityUnitId,
      weightChangeNumber: selectedItem?.weight,
      quantityUnitCode: findedQtyUnit?.code,
      quantityUnitName: findedQtyUnit?.name,
    };

    const docName = currentDocType.name;
    const payload = extractCopyPayload(selectedValues, value, defVals, docName, currentDocType.documentTypeId);
    const isCustomerOrderToPurchaseOrder =
      parseInt(currentDocType.documentTypeId) === DocumentType.CustomerOrder &&
      [DocumentType.PurchaseOrder, DocumentType.PurchaseOrderVariant].includes(parseInt(value));

    if (isCustomerOrderToPurchaseOrder) {
      const firstLineItem = await getItemMetaByCode(payload?.docLine?.[0]?.itemCode);
      const defaultSupplierId = firstLineItem?.defSupplier ?? null;

      if (defaultSupplierId) {
        const defaultSupplier = await masterDataService.companyToFormById(defaultSupplierId).catch(() => null);

        payload.companyId = defaultSupplierId;
        payload.companyCode = defaultSupplier?.code ?? '';
        payload.companyName = defaultSupplier?.name ?? '';
      }
    }

    const route = DocumentTypeRoutes[value];
    const formName = options.find((option) => option.value === value)?.label;

    setPages({ subMenuName: formName });
    navigate(`/app/${route}`, {
      state: {
        targetAdd: payload,
      },
    });
  };

  const getDocumentById = () => {
    documentService.getDocumentById(selectedValues.docId, docTypeId).then((data) => {
      const updatedDocLine = data.docLine.map((line) => {
        const existingLine = selectedValues.docLine.find(
          (oldLine) => oldLine.docId === line.docId && oldLine.lineNum === line.lineNum
        );
        return {
          ...line,
          deletable: existingLine?.deletable ?? true,
        };
      });

      setSelectedValues({
        ...data,
        docLine: updatedDocLine,
      });
    });
  };

  const checkReferenced = () => {
    if (!selectedValues.docId || currentDocType?.inventoryAccounting || selectedValues.docStatus !== DocStatus.OPENED) {
      return;
    }

    const body = { docId: selectedValues.docId, docTypeId: parseInt(docTypeId), targetDocTypeIds: targetTypeIds };

    documentService.checkReferenced(body).then((data) => {
      setSelectedValues((prev) => {
        const updatedDocLines = prev.docLine.map((line) => {
          const isReferenced = data.includes(line.lineNum);

          const deletable = !isReferenced;

          return line.deletable === deletable ? line : { ...line, deletable };
        });

        const changed = updatedDocLines.some((ln, i) => ln !== prev.docLine[i]);
        if (!changed) return prev;

        return { ...prev, docLine: updatedDocLines };
      });
    });
  };

  const getDownPaymentDoc = (companyId) => {
    setDownPaymentDialogOpen(true);
    documentService.getDownPayments(companyId).then((data) => {
      setDownPaymentDialogData(data);
    });
  };

  const getDocData = (selectedDocTypeId) => {
    setDocTableDialogOpen(true);

    setSourceDatagridIdentifiers(getSourceDocDataGridIdentifiers(selectedDocTypeId));

    const foundDocName = docTypes.find((docType) => docType.documentTypeId === selectedDocTypeId)?.name;
    setDocName(foundDocName);

    const initialState = {
      filter: {
        filterModel: {
          items: [
            {
              field: 'companyId',
              operator: 'is',
              value: companyId,
            },
            {
              field: 'docStatus',
              operator: 'is',
              value: DocStatus.OPENED,
            },
          ],
          logicOperator: 'and',
        },
      },
    };
    //@TODO a tétel sorokat külön lekérni, amint megvannak a választott docId-k.
    documentService.getDocuments(initialState, selectedDocTypeId).then((data) => {
      setDocDialogData(data);
    });
  };

  const handleNavigate = () => {
    setPages({ subMenuName: submenuNames[0] });
    navigate(`/app/docContactMap/${selectedValues.docTypeId}/${selectedValues.docId}`, {
      state: { docName: currentDocType.name },
    });
  };

  const onAddClick = () => {
    setSelectedValues({});
    setViewField(false);
    setAddField(false);
    setAttachmentEditMode(false);
  };

  const onModifyClick = () => {
    if (selectedValues.docStatus === DocStatus.CLOSED) {
      setAttachmentEditMode(true);
    } else {
      setViewField(false);
      setAttachmentEditMode(false);
    }
    setEditButtonvisible(false);
    getDocumentById();
    setReqDate(dayjs());
  };

  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const onCopyClick = () => {
    setSelectedValues((prev) => ({
      ...prev,
      docNum: '',
      mode: 'copy',
      balanceSettled: false,
      settledAmount: '',
      openAmount: '',
      settlement: [],
    }));
    setViewField(false);
    setAddField(false);
    setAttachmentEditMode(false);
  };

  const hasTargetDownPaymentRequest = () => {
    documentService.hasTargetDownPaymentRequest(selectedValues.docId).then((data) => {
      if (Object.keys(data).length > 0) {
        setSelectedValues((prev) => ({
          ...prev,
          hasTarget: true,
        }));
      } else {
        setSelectedValues((prev) => ({
          ...prev,
          hasTarget: false,
        }));
      }
    });
  };

  const handleSelectOperation = (value) => {
    if (value === Operation.STORNO) {
      setConfirmDialogOpen(true);
    } else {
      setCloseConfirmDialogOpen(true);
    }
  };

  const checkActualDocRow = async () => {
    const doc = getDocumentById();

    if (
      doc &&
      (doc.docStatus !== DocStatus.OPENED || doc.docLine?.some((line) => line.lineStatus !== DocStatus.OPENED))
    ) {
      addNotification({
        content: 'Bizonylat nem stornózható, nem nyitott!',
        type: NotificationType.ERROR,
      });
      return false;
    }
    setConfirmDialogOpen(false);
    return true;
  };

  const checkHasTargetDoc = () => {
    const isReference = checkReferenced();

    if (isReference) {
      addNotification({
        content: 'A bizonylat nem stornózható, létezik célbizonylat!',
        type: NotificationType.ERROR,
      });
      return true;
    }
    return false;
  };

  const handleDocStorno = async () => {
    const normalizedDoc = {
      ...selectedValues,
      docLine: (selectedValues.docLine ?? []).map((line) => {
        const { docLineBatchSn: normalizedBatches, docLineBin: normalizedBins } = normalizeDocLineBatchBin(line);

        return {
          ...line,
          docLineBatchSn: normalizedBatches,
          docLineBin: normalizedBins,
        };
      }),
    };

    const payload = extractCopyPayload(normalizedDoc);
    await documentService.docStorno(selectedValues.docId, docTypeId, payload).then((data) => {
      setRefetchTable(true);
      setSelectedValues(data);
      setRowSelectionModel([data.docId]);
    });
  };

  const selectCompanyFromDialog = async (companyId, setFieldValue) => {
    setCompanyDialogOpen(false);
    setCompanyDialogSelectedCompanyId(null);
    setCompanyDialogHideRightPanel(false);
    setCompanyDialogSelectOnClose(true);

    if (!companyId) return;

    const company = await masterDataService.companyToFormById(companyId);

    if (!company) {
      addNotification({
        content: 'Hiba történt az Üzleti partner adatainak lekérésekor!',
        type: NotificationType.ERROR,
      });
      return;
    }

    const isIncoming = documentData.direction === DocumentDirection.INCOMING;

    if (isIncoming && !company.supplier) {
      addNotification({
        content: 'Kérem sorolja be szállítónak az Üzleti partnert!',
        type: NotificationType.ERROR,
      });
      setFieldValue('companyId', null);
      return;
    }

    if (!isIncoming && !company.customer) {
      addNotification({
        content: 'Kérem sorolja be vevőnek az Üzleti partnert!',
        type: NotificationType.ERROR,
      });
      setFieldValue('companyId', null);
      return;
    }

    const companyIndex = autocompleteData.company.findIndex((c) => c.id === company.id);

    const newAutocompleteData = {
      ...autocompleteData,
      company:
        companyIndex !== -1
          ? autocompleteData.company.map((c) => (c.id === company.id ? company : c))
          : [...autocompleteData.company, company],
    };
    setAutoCompleteData(newAutocompleteData);

    setFieldValue('companyId', companyId);
  };

  const selectProjectFromDialog = async (projectId, setFieldValue) => {
    setProjectDialogOpen(false);

    if (!projectId) return;

    const findedProject = autocompleteData.project.find((i) => i.projectId === projectId);

    if (!findedProject) {
      const project = await masterDataService.projectToFormById(projectId);

      if (!project) {
        addNotification({
          content: 'Hiba történt a Projekt adatainak lekérésekor!',
          type: NotificationType.ERROR,
        });
      }

      setAutoCompleteData((prev) => ({
        ...prev,
        project: [...prev.project, project],
      }));

      setAutoCompleteOptions((prev) => ({
        ...prev,
        project: [
          ...prev.project,
          {
            label: `${project.projectName} - ${project.orderNumber}`,
            value: project.projectId,
          },
        ],
      }));
    }

    setFieldValue('projectId', projectId);
  };

  const selectCostCenterFromDialog = async (costCenterCode, setFieldValue) => {
    setCostCenterDialogOpen(false);

    if (!costCenterCode) return;

    const findedCostCenter = autocompleteData.costCenter.find((i) => i.code === costCenterCode);

    if (!findedCostCenter) {
      const costCenter = await masterDataService.costCenterToFormByCode(costCenterCode);

      if (!costCenter) {
        addNotification({
          content: 'Hiba történt a Költséghely adatainak lekérésekor!',
          type: NotificationType.ERROR,
        });
      }

      setAutoCompleteData((prev) => ({
        ...prev,
        costCenter: [...prev.costCenter, costCenter],
      }));

      setAutoCompleteOptions((prev) => ({
        ...prev,
        costCenter: [
          ...prev.costCenter,
          {
            label: `${costCenter.code} - ${costCenter.name}`,
            value: costCenter.code,
          },
        ],
      }));
    }

    setFieldValue('costCenterCode', costCenterCode);
  };

  const docTotal = (values, setFieldValue) => {
    const toNumber = (value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const ownCur = autocompleteData.currency.find((c) => c.code === config?.currencyCode);
    const docCur = autocompleteData.currency.find((c) => c.code === values.currencyCode);

    const lines = values.docLine ?? [];
    //mathjs
    const totalNetPrice = round(
      lines.reduce((acc, line) => acc + toNumber(line.netPrice), 0),
      docCur?.amountRounding ?? 0
    );
    const totalVatValue = round(
      lines.reduce((acc, line) => acc + toNumber(line.vatValue), 0),
      docCur?.amountRounding ?? 0
    );
    const totalGrossPrice = round(
      lines.reduce((acc, line) => acc + toNumber(line.grossPrice), 0),
      docCur?.amountRounding ?? 0
    );
    const totalNetPriceInOwnCur = round(
      lines.reduce((acc, line) => acc + toNumber(line.netPriceInOwnCur), 0),
      ownCur?.amountRounding ?? 0
    );
    const totalVatValueInOwnCur = round(
      lines.reduce((acc, line) => acc + toNumber(line.vatValueInOwnCur), 0),
      ownCur?.amountRounding ?? 0
    );

    // ha a saját pénznem és a dokumentum pénzneme megegyezik, akkor a saját bruttó legyen ugyan az mint a kerekítés után a bizonylat pénznemében

    const totalGrossWithRounding = round(
      add(totalGrossPrice, toNumber(values.roundingInDocCur)),
      docCur?.amountRounding ?? 0
    );

    // árfolyammal szorozni
    const totalGrossPriceInOwnCurWithRounding = round(
      multiply(totalGrossWithRounding, toNumber(values.exchangeRate || 1)),
      ownCur?.amountRounding ?? 0
    );

    setFieldValue('sumInDocCur', totalGrossWithRounding);
    setFieldValue('netInDocCur', totalNetPrice);
    setFieldValue('VATInDocCur', totalVatValue);
    //setFieldValue('roundingInDocCur', );

    setFieldValue('sumInOwnCur', totalGrossPriceInOwnCurWithRounding);
    setFieldValue('netInOwnCur', totalNetPriceInOwnCur);
    setFieldValue('VATInOwnCur', totalVatValueInOwnCur);
  };

  const showValidationSummary = (errors) => {
    setValidationDialogState({
      open: true,
      errors,
    });
  };

  const handleDraftSubmit = async (helpers) => {
    const errors = await helpers.validateForm();

    if (hasNestedErrors(errors)) {
      helpers.setTouched(buildTouchedFromErrors(errors), false);
      showValidationSummary(errors);
      return;
    }

    await handleSubmit(helpers.values, helpers, true);
  };

  const handleCompanyViewOpen = (selectedCompanyId) => {
    if (!selectedCompanyId) return;

    setCompanyDialogSelectedCompanyId(selectedCompanyId);
    setCompanyDialogHideRightPanel(true);
    setCompanyDialogSelectOnClose(false);
    setCompanyDialogOpen(true);
  };

  return (
    <>
      {Object.keys(autocompleteData).length > 0 && Object.keys(defaultValues).length > 0 && (
        <div className="h-full overflow-auto custom-scrollbar">
          <div className="flex items-center flex-wrap">
            <FormHeaderUI
              addClick={!dialog ? onAddClick : null}
              addDisabled={!viewField || !canEdit || attachmentEditMode}
              modClick={!dialog ? onModifyClick : null}
              modDisabled={
                !viewField ||
                attachmentEditMode ||
                operatorsDisabled ||
                !canEdit ||
                (selectedValues.docStatus !== DocStatus.OPENED &&
                  selectedValues.docStatus !== DocStatus.CLOSED &&
                  selectedValues.docStatus !== DocStatus.CANCELLATION &&
                  selectedValues.docStatus !== DocStatus.PLANNED)
              }
              deleteClick={!dialog ? onDeleteClick : null}
              deleteDisabled={
                !viewField ||
                attachmentEditMode ||
                operatorsDisabled ||
                !canEdit ||
                (!canDelete && selectedValues.docStatus !== DocStatus.PLANNED)
              }
              copyClick={!dialog ? onCopyClick : null}
              copyDisabled={!viewField || attachmentEditMode || operatorsDisabled || !canEdit || !canCopy}
            />
            {!dialog && (
              <>
                <Box ml="0.5rem" className="flex items-stretch">
                  <ButtonUI
                    text="Nyomtatás"
                    size="sm"
                    className="bg-labelColor py-3 rounded-r-none"
                    onClick={() => printClick()}
                    disabled={
                      !viewField || attachmentEditMode || operatorsDisabled || availablePrintReports.length === 0
                    }
                  />
                  <button
                    type="button"
                    className="rounded-r-md rounded-l-none bg-labelColor px-3 text-white border-l border-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleOpenPrintReportMenu}
                    disabled={
                      !viewField || attachmentEditMode || operatorsDisabled || availablePrintReports.length === 0
                    }
                    title={
                      selectedPrintReport
                        ? getPrintReportLabel(selectedPrintReport)
                        : 'Nem áll rendelkezésre nyomtatási kép'
                    }
                  >
                    <ArrowDropDownIcon fontSize="small" />
                  </button>
                  <Menu
                    anchorEl={printReportAnchorEl}
                    open={Boolean(printReportAnchorEl)}
                    onClose={handleClosePrintReportMenu}
                  >
                    {availablePrintReports.map((printReport) => {
                      const isSelected = Number(printReport.id) === Number(selectedPrintReport?.id);

                      return (
                        <MenuItem key={printReport.id} onClick={() => handleSelectPrintReport(printReport.id)}>
                          <ListItemIcon sx={{ minWidth: '2rem' }}>
                            {isSelected ? <CheckIcon fontSize="small" /> : null}
                          </ListItemIcon>
                          <ListItemText>{getPrintReportLabel(printReport)}</ListItemText>
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Box>
                {supportingDocumentAssignmentEnabled && parseInt(docTypeId) === DocumentType.Delivery && (
                  <Box ml="0.5rem">
                    <ButtonUI
                      text="Kísérő dokumentumok"
                      size="sm"
                      className="bg-labelColor py-3"
                      type="button"
                      onClick={() => setSupportingDocumentDialogOpen(true)}
                      disabled={!viewField || attachmentEditMode || operatorsDisabled || !selectedValues?.docId}
                    />
                  </Box>
                )}
                {config?.emailSendingEnabled && canEmailSend && hasEmailTemplate && (
                  <Box ml="0.5rem">
                    <ButtonUI
                      text="E-mail küldés"
                      size="sm"
                      className="bg-primary py-3"
                      type="button"
                      onClick={() => setEmailDialogOpen(true)}
                      disabled={
                        !viewField ||
                        attachmentEditMode ||
                        operatorsDisabled ||
                        !selectedValues?.docId ||
                        availablePrintReports.length === 0
                      }
                    />
                  </Box>
                )}
                <Box ml="0.5rem">
                  <DropDownUI
                    text="Cél"
                    icon="Add"
                    options={options}
                    onSelect={(newVal) => onSelect(newVal.value)}
                    disabled={
                      (parseInt(docTypeId) === DocumentType.DownPaymentRequest
                        ? selectedValues?.hasTarget
                        : selectedValues.docStatus !== DocStatus.OPENED) ||
                      !viewField ||
                      attachmentEditMode ||
                      operatorsDisabled
                    }
                  />
                </Box>
                <Box ml="0.5rem">
                  <DropDownUI
                    text="Forrás"
                    icon="Add"
                    options={sourceOptions}
                    onSelect={(newVal) => getDocData(newVal.value)}
                    disabled={
                      viewField || attachmentEditMode || !companyId || !hasNoLines || sourceTypeIds.length === 0
                    }
                  />
                </Box>
                <Box ml="0.5rem">
                  <DropDownUI
                    text="Művelet"
                    options={OperationList(selectedValues)}
                    onSelect={(newVal) => handleSelectOperation(newVal.value)}
                    disabled={!viewField || attachmentEditMode || operatorsDisabled}
                  />
                </Box>
                <Box ml="0.5rem">
                  <IconButton onClick={handleNavigate} disabled={!selectedValues.docId || attachmentEditMode}>
                    {getIcon('AltRoute', '1.7rem', selectedValues.docId ? '#0076f8' : undefined)}
                  </IconButton>
                </Box>
              </>
            )}
          </div>
          <Formik
            initialValues={initValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={(values, helpers) => handleSubmit(values, helpers, false)}
            enableReinitialize
          >
            {(helpers) => {
              const { resetForm, values, submitForm } = helpers;
              if (values.docLine?.length > 0) {
                setHasNoLines(false);
              } else {
                setHasNoLines(true);
              }

              if (values.companyId) {
                setCompanyId(values.companyId);
              } else {
                setCompanyId(null);
              }

              if (values.langCode) {
                setDocumentLangCode(values.langCode);
              } else {
                setDocumentLangCode(null);
              }

              return (
                <Form
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.ctrlKey) {
                      e.preventDefault();
                    }

                    if (e.key === 'Enter' && e.ctrlKey) {
                      e.preventDefault();
                      submitForm();
                    }
                  }}
                >
                  <ValidationSummaryWatcher onValidationFailed={showValidationSummary} />

                  <DocumentFields
                    viewField={viewField || dialog}
                    selectedValues={selectedValues}
                    autocompleteOptions={autocompleteOptions}
                    autocompleteData={autocompleteData}
                    documentData={documentData}
                    selectedCompanyState={{ selectedCompany, setSelectedCompany }}
                    companyContactsState={{ companyContacts, setCompanyContacts }}
                    docTypeId={docTypeId}
                    setSettlementDialogOpen={setSettlementDialogOpen}
                    getDownPaymentDoc={getDownPaymentDoc}
                    editButtonVisible={editButtonVisible}
                    currentDocType={currentDocType}
                    setCompanyDialogOpen={setCompanyDialogOpen}
                    onCompanyViewOpen={handleCompanyViewOpen}
                    setBulkUpdateDialogState={setBulkUpdateDialogState}
                    setProjectDialogOpen={setProjectDialogOpen}
                    setCostCenterDialogOpen={setCostCenterDialogOpen}
                  />

                  {!isLoading && (
                    <DocumentTabs
                      viewField={viewField || dialog}
                      setRefetchTable={setRefetchTable}
                      selectedValues={selectedValues}
                      user={user}
                      disabled={viewField || dialog}
                      attachmentEditMode={attachmentEditMode}
                      defaultField={defaultDocumentLine}
                      autocompleteOptions={autocompleteOptions}
                      autocompleteData={autocompleteData}
                      documentData={documentData}
                      selectedCompanyState={{ selectedCompany, setSelectedCompany }}
                      companyContactsState={{ companyContacts, setCompanyContacts }}
                      docTypeId={docTypeId}
                      datagridIdentifiers={datagridIdentifiers}
                      jokerFields={jokerFields}
                      jokerLineFields={jokerLineFields}
                      upperDocType={upperDocType}
                      currentDocType={currentDocType}
                      batchLineState={{ batchLine, setBatchLine }}
                      binLineState={{ binLine, setBinLine }}
                      setNoteDialogState={setNoteDialogState}
                      docTypes={docTypes}
                      setAutoCompleteData={setAutoCompleteData}
                      setAutoCompleteOptions={setAutoCompleteOptions}
                      newFilesState={{ newFiles, setNewFiles }}
                      docTotal={docTotal}
                    />
                  )}

                  <DocumentEmailDialog
                    open={emailDialogOpen}
                    onClose={() => setEmailDialogOpen(false)}
                    onSend={handleSendDocumentEmail}
                    config={config}
                    user={user}
                    documentValues={values}
                    selectedCompany={selectedCompany}
                    selectedPrintReport={selectedPrintReport}
                    currentDocType={currentDocType}
                  />

                  <CompanyDialog
                    showDialog={companyDialogOpen}
                    onClose={selectCompanyFromDialog}
                    selectedCompanyId={companyDialogSelectedCompanyId}
                    hideRightPanel={companyDialogHideRightPanel}
                    selectOnClose={companyDialogSelectOnClose}
                  />
                  <ProjectDialog showDialog={projectDialogOpen} onClose={selectProjectFromDialog} />
                  <CostCenterDialog showDialog={costCenterDialogOpen} onClose={selectCostCenterFromDialog} />

                  <FormButtonPanelUI
                    onCancelClick={() => {
                      valuesToFormRunRef.current += 1;
                      setViewField(true);
                      setEditButtonvisible(true);
                      setAddField(true);
                      setAttachmentEditMode(false);
                      setBulkUpdateDialogState(defaultBulkUpdateDialogState);
                      setValidationDialogState({ open: false, errors: null });
                      if (selectedValues?.mode === 'copy') {
                        resetForm({ values: { ...defaultValues } });
                        setSelectedValues({});
                        setRowSelectionModel([]);
                      } else {
                        resetForm();
                      }
                      setNewFiles([]);
                    }}
                    disabled={(editButtonVisible && addField) || helpers.isSubmitting}
                    onSaveClick={() => {
                      handleDraftSubmit(helpers);
                    }}
                    saveDisabled={(addField && selectedValues.docStatus !== DocStatus.PLANNED) || helpers.isSubmitting}
                  />

                  <BatchDialog
                    selectedLine={batchLine}
                    open={batchLine}
                    onClose={() => {
                      setBatchLine(null);
                      setBinLine(null);
                    }}
                    documentData={documentData}
                    autocompleteData={autocompleteData}
                    defaultBatchLine={defaultBatchLine}
                  />

                  <SettlementDialog
                    documentData={documentData}
                    open={settlementDialogOpen}
                    onClose={() => setSettlementDialogOpen(false)}
                    selectedValuesState={{ selectedValues, setSelectedValues }}
                    setRefetchTable={setRefetchTable}
                    defaultSettlementLine={defaultSettlementLine}
                    autocompleteOptions={autocompleteOptions}
                    autocompleteData={autocompleteData}
                    calculateBalance={calculateBalance}
                  />

                  <DownPaymentTableDialog
                    open={downPaymentDialogOpen}
                    onClose={() => setDownPaymentDialogOpen(false)}
                    data={downPaymentDialogData}
                    setSelectedValues={setSelectedValues}
                    defaultDocumentLine={defaultDocumentLine}
                  />

                  <NoteDialog
                    open={noteDialogState.open}
                    onClose={() => setNoteDialogState({ open: false, valueIndex: null })}
                    noteDialogState={noteDialogState}
                  />

                  <BulkUpdateFieldDialog
                    dialogState={bulkUpdateDialogState}
                    setDialogState={setBulkUpdateDialogState}
                  />

                  <ConfirmDialogUI
                    show={closeConfirmDialogOpen}
                    onHide={() => setCloseConfirmDialogOpen(false)}
                    handleChangeElement={async () => {
                      const toCloseValues = {
                        ...values,
                        docStatus: DocStatus?.CLOSED,
                        docLine: (values.docLine ?? []).map((dl) => ({
                          ...dl,
                          lineStatus: DocStatus?.CLOSED,
                        })),
                      };

                      updateObject(toCloseValues);

                      setCloseConfirmDialogOpen(false);
                    }}
                    title="Bezárás"
                    message="A bizonylat bezárása nem vonható vissza! Folytatja?"
                    confirmButtonText="Folytatás"
                  />
                </Form>
              );
            }}
          </Formik>
        </div>
      )}
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />

      <SourceDocTableDialog
        open={docTableDialogOpen}
        onClose={() => setDocTableDialogOpen(false)}
        docName={docName}
        data={docDialogData}
        sourceDatagridIdentifiers={sourceDatagridIdentifiers}
        setSelectedDocRows={setSelectedDocRows}
        setDocLineTableDialogOpen={setDocLineTableDialogOpen}
      />

      <SourceDocLineTableDialog
        open={docLineTableDialogOpen}
        onClose={() => setDocLineTableDialogOpen(false)}
        docName={docName}
        data={selectedDocRows}
        sourceDatagridIdentifiers={sourceDatagridIdentifiers}
        docTypeId={docTypeId}
        setSelectedValues={setSelectedValues}
      />

      <ConfirmDialogUI
        show={confirmDialogOpen}
        onHide={() => setConfirmDialogOpen(false)}
        handleChangeElement={async () => {
          const canProceed = await checkActualDocRow();
          if (!canProceed) return;

          const hasTarget = await checkHasTargetDoc();
          if (hasTarget) return;
          handleDocStorno();
        }}
        title="Bizonylat stornó"
        message="A bizonylat stornózása nem vonható vissza! Folytatja?"
        confirmButtonText="Folytatás"
      />

      <ValidationSummaryDialog
        open={validationDialogState.open}
        onClose={() => setValidationDialogState((prev) => ({ ...prev, open: false }))}
        errors={validationDialogState.errors}
        sectionRules={documentValidationSections}
        fieldLabels={documentValidationFieldLabels}
      />
      <SupportingDocumentDialog
        open={supportingDocumentDialogOpen}
        onClose={() => setSupportingDocumentDialogOpen(false)}
        docId={selectedValues?.docId}
        documentTypeId={parseInt(docTypeId)}
      />
    </>
  );
};

export default DocumentPage;
